import type { Prayer } from '../types/prayer';
import {
  GENERAL_KEY,
  isGeneral,
  isSelf,
  peopleInPrayerWithMap,
  whoKey,
  whoOfWithMap,
  type WhoMergeMap,
} from './who-match';
import {
  compareDiscoveryStrings,
  discoveryField,
  prepareDiscoveryQuery,
  scoreDiscoveryFields,
  type DiscoveryField,
} from './discovery-search';
import { commonPrefixLength, osaDistanceWithin } from './edit-distance';

export type PeopleSearchFilter = 'all' | 'month' | '90d' | 'answered';

export interface PeopleArchiveEntry {
  identityKey: string;
  who: string;
  displayName: string;
  count: number;
  last: string | null;
  lastMs: number | null;
  hasAnswered: boolean;
  searchField: DiscoveryField;
}

export interface PeopleSearchMetrics {
  prayerVisits: number;
  membershipVisits: number;
  normalizationCount: number;
  comparisonCount: number;
  retainedBytesEstimate: number;
}

export interface PeopleSearchIndex {
  readonly scopeKey: string;
  readonly entries: readonly PeopleArchiveEntry[];
  readonly movingPrayerCount: number;
  readonly retainedBytesEstimate: number;
}

export interface PeopleSearchResult {
  entries: PeopleArchiveEntry[];
  fuzzy: boolean;
}

export function createPeopleSearchMetrics(): PeopleSearchMetrics {
  return {
    prayerVisits: 0,
    membershipVisits: 0,
    normalizationCount: 0,
    comparisonCount: 0,
    retainedBytesEstimate: 0,
  };
}

function finiteTime(value: unknown): number | null {
  if (typeof value !== 'string') return null;
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : null;
}

function latestTouch(prayer: Prayer): number | null {
  let latest = -Infinity;
  for (const value of [prayer.createdAt, prayer.updatedAt]) {
    const time = finiteTime(value);
    if (time !== null && time > latest) latest = time;
  }
  for (const entry of prayer.progressHistory ?? []) {
    const time = finiteTime(entry.date);
    if (time !== null && time > latest) latest = time;
  }
  return Number.isFinite(latest) ? latest : null;
}

interface MutablePersonEntry {
  identityKey: string;
  who: string;
  count: number;
  lastMs: number | null;
  hasAnswered: boolean;
}

function updateEntry(
  entries: Map<string, MutablePersonEntry>,
  who: string,
  touch: number | null,
  answered: boolean,
): void {
  const identityKey = isGeneral(who) ? 'general' : whoKey(who);
  if (!identityKey) return;
  const existing = entries.get(identityKey);
  if (existing) {
    existing.count += 1;
    existing.hasAnswered ||= answered;
    if (touch !== null && (existing.lastMs === null || touch > existing.lastMs)) {
      existing.lastMs = touch;
    }
    return;
  }
  entries.set(identityKey, {
    identityKey,
    who: isGeneral(who) ? GENERAL_KEY : who,
    count: 1,
    lastMs: touch,
    hasAnswered: answered,
  });
}

export function buildPeopleSearchIndex(
  prayers: readonly Prayer[],
  mergeMap: WhoMergeMap,
  scopeKey: string,
  labels: { self: string; general: string },
  metrics?: PeopleSearchMetrics,
): PeopleSearchIndex {
  const entries = new Map<string, MutablePersonEntry>();
  let movingPrayerCount = 0;

  for (const prayer of prayers) {
    if (metrics) metrics.prayerVisits += 1;
    const answered = (prayer.status ?? 'Open') === 'Answered';
    if ((prayer.status ?? 'Open') === 'Moving') movingPrayerCount += 1;
    const touch = latestTouch(prayer);
    const primary = whoOfWithMap(prayer, mergeMap);
    if (isGeneral(primary)) updateEntry(entries, GENERAL_KEY, touch, answered);

    for (const who of peopleInPrayerWithMap(prayer, mergeMap)) {
      if (metrics) metrics.membershipVisits += 1;
      if (isGeneral(who)) continue;
      updateEntry(entries, who, touch, answered);
    }
  }

  let retainedBytesEstimate = 0;
  const finalized = Array.from(entries.values(), (entry): PeopleArchiveEntry => {
    const displayName = isSelf(entry.who)
      ? labels.self
      : isGeneral(entry.who)
        ? labels.general
        : entry.who;
    const searchField = discoveryField(displayName);
    if (metrics) metrics.normalizationCount += 1;
    retainedBytesEstimate +=
      (entry.identityKey.length + entry.who.length + displayName.length +
        searchField.normalized.length) * 2 +
      searchField.tokens.length * 8 +
      104;
    return {
      identityKey: entry.identityKey,
      who: entry.who,
      displayName,
      count: entry.count,
      last: entry.lastMs === null ? null : new Date(entry.lastMs).toISOString(),
      lastMs: entry.lastMs,
      hasAnswered: entry.hasAnswered,
      searchField,
    };
  });
  if (metrics) metrics.retainedBytesEstimate = retainedBytesEstimate;
  return { scopeKey, entries: finalized, movingPrayerCount, retainedBytesEstimate };
}

function pinRank(entry: PeopleArchiveEntry): number {
  if (isSelf(entry.who)) return 0;
  if (isGeneral(entry.who)) return 1;
  return 2;
}

function matchesFilter(
  entry: PeopleArchiveEntry,
  filter: PeopleSearchFilter,
  nowMs: number,
): boolean {
  if (filter === 'all') return true;
  if (filter === 'answered') return entry.hasAnswered;
  const lastMs = entry.lastMs;
  if (lastMs === null) return false;
  if (filter === 'month') {
    const now = new Date(nowMs);
    return lastMs >= new Date(now.getFullYear(), now.getMonth(), 1).getTime();
  }
  return lastMs >= nowMs - 90 * 86_400_000;
}

function searchPeopleIndexExact(
  index: PeopleSearchIndex,
  queryValue: string,
  filter: PeopleSearchFilter,
  nowMs = Date.now(),
  metrics?: PeopleSearchMetrics,
): PeopleArchiveEntry[] {
  const query = prepareDiscoveryQuery(queryValue);
  if (!query.valid) return [];
  if (query.normalized !== '' && query.tokens.length === 0) return [];
  const ranked: Array<{ entry: PeopleArchiveEntry; score: number }> = [];
  for (const entry of index.entries) {
    if (!matchesFilter(entry, filter, nowMs)) continue;
    let score = 0;
    if (query.tokens.length > 0) {
      if (metrics) metrics.comparisonCount += query.tokens.length;
      const matched = scoreDiscoveryFields(query, [entry.searchField]);
      if (matched === null) continue;
      score = matched + (entry.searchField.normalized === query.normalized ? 1000 : 0);
    }
    ranked.push({ entry, score });
  }

  ranked.sort((left, right) => {
    if (right.score !== left.score) return right.score - left.score;
    const pin = pinRank(left.entry) - pinRank(right.entry);
    if (pin !== 0) return pin;
    const leftTime = left.entry.lastMs ?? 0;
    const rightTime = right.entry.lastMs ?? 0;
    if (rightTime !== leftTime) return rightTime - leftTime;
    return compareDiscoveryStrings(left.entry.identityKey, right.entry.identityKey);
  });
  return ranked.map(({ entry }) => entry);
}

interface FuzzyPeopleCandidate {
  entry: PeopleArchiveEntry;
  distance: number;
  directAnchors: number;
  prefix: number;
  signature: string;
}

function fuzzyPeopleCandidate(
  entry: PeopleArchiveEntry,
  query: ReturnType<typeof prepareDiscoveryQuery>,
  metrics?: PeopleSearchMetrics,
): FuzzyPeopleCandidate | null {
  // Reserved identities are translated concepts rather than user-entered
  // names. Guessing one from a typo could open the wrong memorial.
  if (isSelf(entry.who) || isGeneral(entry.who)) return null;

  let distance = 0;
  let directAnchors = 0;
  let prefix = 0;
  const signature: string[] = [];
  for (const word of query.tokens) {
    if (metrics) metrics.comparisonCount += 1;
    const direct = scoreDiscoveryFields(
      { normalized: word, tokens: [word], valid: true },
      [entry.searchField],
    );
    if (direct !== null) {
      directAnchors += 1;
      prefix += word.length;
      signature.push(word);
      continue;
    }
    if (word.length < 4) return null;

    let bestToken: string | null = null;
    let bestPrefix = -1;
    for (const token of entry.searchField.tokens) {
      if (Math.abs(token.length - word.length) > 1) continue;
      const tokenDistance = osaDistanceWithin(word, token, 1);
      if (tokenDistance > 1) continue;
      const tokenPrefix = commonPrefixLength(word, token);
      if (
        bestToken === null ||
        tokenPrefix > bestPrefix ||
        (tokenPrefix === bestPrefix && token < bestToken)
      ) {
        bestToken = token;
        bestPrefix = tokenPrefix;
      }
    }
    if (bestToken === null) return null;
    distance += 1;
    prefix += bestPrefix;
    signature.push(bestToken);
  }
  if (query.tokens.length > 1 && directAnchors === 0) return null;
  return {
    entry,
    distance,
    directAnchors,
    prefix,
    signature: signature.join('\u0000'),
  };
}

function searchPeopleIndexFuzzy(
  index: PeopleSearchIndex,
  queryValue: string,
  filter: PeopleSearchFilter,
  nowMs: number,
  metrics?: PeopleSearchMetrics,
): PeopleArchiveEntry[] {
  const query = prepareDiscoveryQuery(queryValue);
  if (!query.valid || query.tokens.length === 0) return [];
  const candidates: FuzzyPeopleCandidate[] = [];
  for (const entry of index.entries) {
    if (!matchesFilter(entry, filter, nowMs)) continue;
    const candidate = fuzzyPeopleCandidate(entry, query, metrics);
    if (candidate) candidates.push(candidate);
  }
  if (candidates.length === 0) return [];
  candidates.sort((left, right) => {
    if (left.distance !== right.distance) return left.distance - right.distance;
    if (right.directAnchors !== left.directAnchors) {
      return right.directAnchors - left.directAnchors;
    }
    if (right.prefix !== left.prefix) return right.prefix - left.prefix;
    const leftTime = left.entry.lastMs ?? 0;
    const rightTime = right.entry.lastMs ?? 0;
    if (rightTime !== leftTime) return rightTime - leftTime;
    return compareDiscoveryStrings(left.entry.identityKey, right.entry.identityKey);
  });

  const best = candidates[0];
  const equallyStrongSignatures = new Set(
    candidates
      .filter(
        (candidate) =>
          candidate.distance === best.distance &&
          candidate.directAnchors === best.directAnchors &&
          candidate.prefix === best.prefix,
      )
      .map((candidate) => candidate.signature),
  );
  // "Jonn" must not guess between John and Joan. Multiple entries named
  // Sarah may all surface for "Srah" because they express one clear intended
  // spelling while preserving their distinct identity keys.
  if (equallyStrongSignatures.size !== 1) return [];
  return candidates
    .filter(
      (candidate) =>
        candidate.distance === best.distance &&
        candidate.directAnchors === best.directAnchors &&
        candidate.prefix === best.prefix &&
        candidate.signature === best.signature,
    )
    .map(({ entry }) => entry);
}

export function searchPeopleIndexWithMeta(
  index: PeopleSearchIndex,
  queryValue: string,
  filter: PeopleSearchFilter,
  nowMs = Date.now(),
  metrics?: PeopleSearchMetrics,
): PeopleSearchResult {
  const exact = searchPeopleIndexExact(index, queryValue, filter, nowMs, metrics);
  const query = prepareDiscoveryQuery(queryValue);
  if (query.normalized === '' || exact.length > 0 || !query.valid) {
    return { entries: exact, fuzzy: false };
  }
  const fuzzy = searchPeopleIndexFuzzy(index, queryValue, filter, nowMs, metrics);
  return { entries: fuzzy, fuzzy: fuzzy.length > 0 };
}

export function searchPeopleIndex(
  index: PeopleSearchIndex,
  queryValue: string,
  filter: PeopleSearchFilter,
  nowMs = Date.now(),
  metrics?: PeopleSearchMetrics,
): PeopleArchiveEntry[] {
  return searchPeopleIndexWithMeta(index, queryValue, filter, nowMs, metrics).entries;
}
